import React from 'react';
import { Icon } from '@iconify/react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableImage = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group aspect-square rounded-xl overflow-hidden bg-muted border border-border">
      {children}
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="absolute top-2 left-2 w-8 h-8 bg-card/90 text-foreground rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-grab active:cursor-grabbing"
        aria-label="Drag to reorder"
      >
        <Icon icon="lucide:grip-vertical" className="text-lg" />
      </button>
    </div>
  );
};

/**
 * Drag-to-reorder image grid. `items` must be an array of objects with a unique `id`.
 * `renderItem(item)` renders the image + any per-item controls (e.g. delete button).
 */
const SortableImageGrid = ({ items, onReorder, renderItem, className = 'grid grid-cols-2 md:grid-cols-4 gap-4' }) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex(item => item.id === active.id);
    const newIndex = items.findIndex(item => item.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(items, oldIndex, newIndex));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map(item => item.id)} strategy={rectSortingStrategy}>
        <div className={className}>
          {items.map(item => (
            <SortableImage key={item.id} id={item.id}>
              {renderItem(item)}
            </SortableImage>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default SortableImageGrid;
